/**
 * Gateway device types — aligned with gateway_devices + related tables.
 * See: supabase/migrations/*_gateway_tables.sql
 */

export type LifecycleStatus =
  | 'unprovisioned'
  | 'provisioned'
  | 'commissioning'
  | 'active'
  | 'inactive'
  | 'revoked'
  | 'decommissioned';

export type OperationalStatus = 'unknown' | 'healthy' | 'degraded' | 'offline';

export type CommandStatus =
  | 'pending'
  | 'executing'
  | 'success'
  | 'failed'
  | 'expired'
  | 'refused'
  | 'verification_required'
  | 'stale_review';

export type CommandCategory = 'diagnostic' | 'lifecycle' | 'protective' | 'commissioning';
export type CommandCriticality = 'low' | 'medium' | 'high';

export type ReleaseStatus = 'draft' | 'testing' | 'released' | 'deprecated';

export interface GatewayDevice {
  id: string;
  claim_code: string;
  claim_code_consumed: boolean;
  property_id: string | null;
  profile_id?: string | null;
  organization_id: string | null;
  mac_address: string | null;
  hardware_model: string;
  firmware_version: string | null;
  lifecycle_status: LifecycleStatus;
  operational_status: OperationalStatus;
  provisioned_at: string | null;
  commissioned_at: string | null;
  last_status_change: string | null;
  last_heartbeat_at: string | null;
  wifi_rssi: number | null;
  free_heap: number | null;
  free_psram: number | null;
  uptime_seconds: number | null;
  boot_count: number | null;
  ntp_synced: boolean | null;
  bus_devices_expected: number | null;
  bus_devices_responding: number | null;
  bus_errors_total: number | null;
  bus_errors_by_device: Record<string, number> | null;
  last_successful_poll: string | null;
  last_successful_upload: string | null;
  pending_buffer_records: number | null;
  buffer_overflow_count: number | null;
  buffer_last_overflow_at: string | null;
  diagnostic_mode: boolean;
  poll_interval_ms: number;
  upload_interval_ms: number;
  heartbeat_interval_ms: number;
  command_poll_interval_ms: number;
  writes_enabled: boolean;
  ota_channel: string;
  last_batch_sequence: number;
  created_at: string;
  updated_at: string;
}

export interface GatewayCommand {
  id: string;
  device_id: string;
  sequence_id: number;
  command_type: string;
  category: CommandCategory;
  criticality: CommandCriticality;
  payload: Record<string, unknown>;
  requires_confirmation: boolean;
  cooldown_seconds: number;
  user_visible_effect: string | null;
  status: CommandStatus;
  ttl_seconds: number;
  execution_timeout_seconds: number;
  created_at: string;
  delivered_at: string | null;
  executed_at: string | null;
  result: Record<string, unknown> | null;
  requested_by: string | null;
  audit_notes: string | null;
  result_received_count: number;
}

export interface GatewayDeviceConfig {
  id: string;
  gateway_device_id: string;
  bus_address: number;
  protocol: 'modbus_rtu' | 'can';
  equipment_type: string;
  display_name: string | null;
  status: string;
  last_successful_poll: string | null;
  consecutive_failures: number;
}

export interface GatewayFirmwareRelease {
  id: string;
  version: string;
  hardware_model: string;
  release_status: ReleaseStatus;
  sha256: string;
  size_bytes: number;
  storage_path: string;
  release_notes: string | null;
  min_previous_version: string | null;
  created_at: string;
  released_at: string | null;
}

export interface GatewayDiagnosticReport {
  id: string;
  device_id: string;
  session_id: string;
  reported_at: string;
  payload: Record<string, unknown>;
}

export interface CommissioningCheckResult {
  name: string;
  passed: boolean;
  message: string;
  critical: boolean;
  overrideable: boolean;
  overridden?: boolean;
  override_note?: string;
  /** Whether this check runs client-side or requires backend */
  source: 'client' | 'server';
}

export interface CommissioningAuditRecord {
  installer_id: string;
  completed_at: string;
  firmware_version: string;
  discovered_device_baseline: GatewayDeviceConfig[];
  bus_devices_expected: number;
  wifi_rssi_at_completion: number | null;
  checklist_results: CommissioningCheckResult[];
  overridden_checks: { check_name: string; note: string; installer_id: string }[];
  dual_mode_comparison_passed: boolean | null;
  commissioning_duration_minutes: number | null;
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

export const LIFECYCLE_STATUS_CONFIG: Record<LifecycleStatus, { label: string; color: string }> = {
  unprovisioned: { label: 'Unprovisioned', color: 'bg-gray-500/10 text-gray-400 border-gray-500/20' },
  provisioned: { label: 'Provisioned', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  commissioning: { label: 'Commissioning', color: 'bg-wt-amber-primary/10 text-wt-amber-primary border-wt-amber-primary/20' },
  active: { label: 'Active', color: 'bg-wt-green-primary/10 text-wt-green-primary border-wt-green-primary/20' },
  inactive: { label: 'Inactive', color: 'bg-gray-500/10 text-gray-400 border-gray-500/20' },
  revoked: { label: 'Revoked', color: 'bg-wt-red-primary/10 text-wt-red-primary border-wt-red-primary/20' },
  decommissioned: { label: 'Decommissioned', color: 'bg-gray-500/10 text-gray-400 border-gray-500/20' },
};

export const OPERATIONAL_STATUS_CONFIG: Record<OperationalStatus, { label: string; dotColor: string; textColor: string }> = {
  unknown: { label: 'Unknown', dotColor: 'bg-gray-400', textColor: 'text-gray-400' },
  healthy: { label: 'Healthy', dotColor: 'bg-wt-green-primary', textColor: 'text-wt-green-primary' },
  degraded: { label: 'Degraded', dotColor: 'bg-wt-amber-primary', textColor: 'text-wt-amber-primary' },
  offline: { label: 'Offline', dotColor: 'bg-wt-red-primary', textColor: 'text-wt-red-primary' },
};

export const COMMAND_STATUS_CONFIG: Record<CommandStatus, { label: string; color: string }> = {
  pending: { label: 'Pending', color: 'bg-gray-500/10 text-gray-400 border-gray-500/20' },
  executing: { label: 'Executing', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  success: { label: 'Success', color: 'bg-wt-green-primary/10 text-wt-green-primary border-wt-green-primary/20' },
  failed: { label: 'Failed', color: 'bg-wt-red-primary/10 text-wt-red-primary border-wt-red-primary/20' },
  expired: { label: 'Expired', color: 'bg-gray-500/10 text-gray-500 border-gray-500/20' },
  refused: { label: 'Refused', color: 'bg-wt-amber-primary/10 text-wt-amber-primary border-wt-amber-primary/20' },
  verification_required: { label: 'Verification Required', color: 'bg-orange-500/10 text-orange-400 border-orange-500/20' },
  stale_review: { label: 'Stale Review', color: 'bg-wt-red-primary/10 text-wt-red-primary border-wt-red-primary/20' },
};

export const RELEASE_STATUS_CONFIG: Record<ReleaseStatus, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'bg-gray-500/10 text-gray-400 border-gray-500/20' },
  testing: { label: 'Testing', color: 'bg-wt-amber-primary/10 text-wt-amber-primary border-wt-amber-primary/20' },
  released: { label: 'Released', color: 'bg-wt-green-primary/10 text-wt-green-primary border-wt-green-primary/20' },
  deprecated: { label: 'Deprecated', color: 'bg-wt-red-primary/10 text-wt-red-primary border-wt-red-primary/20' },
};

// ---------------------------------------------------------------------------
// Command definitions — which commands are available when
// ---------------------------------------------------------------------------

export interface CommandDefinition {
  type: string;
  label: string;
  category: CommandCategory;
  criticality: CommandCriticality;
  /** True = requires writes_enabled on device */
  requiresWritesEnabled: boolean;
  /** True = only available during commissioning */
  commissioningOnly: boolean;
  /** Confirmation text shown before issuing high-criticality commands */
  confirmationText?: string;
}

export const GATEWAY_COMMANDS: CommandDefinition[] = [
  // Diagnostic/lifecycle — always available to installer
  { type: 'diagnostic_request', label: 'Request Diagnostics', category: 'diagnostic', criticality: 'low', requiresWritesEnabled: false, commissioningOnly: false },
  { type: 'ota_check', label: 'Check for Updates', category: 'lifecycle', criticality: 'low', requiresWritesEnabled: false, commissioningOnly: false },
  { type: 'reboot', label: 'Reboot Gateway', category: 'lifecycle', criticality: 'medium', requiresWritesEnabled: false, commissioningOnly: false, confirmationText: 'This will restart the gateway. Data collection will pause briefly.' },

  // Write-gated — require writes_enabled
  { type: 'fault_reset', label: 'Reset Fault', category: 'protective', criticality: 'medium', requiresWritesEnabled: true, commissioningOnly: false, confirmationText: 'This will attempt to clear the current fault on the equipment.' },
  { type: 'inverter_standby', label: 'Inverter Standby', category: 'protective', criticality: 'high', requiresWritesEnabled: true, commissioningOnly: false, confirmationText: 'This will put the inverter into standby mode. The system will stop producing power.' },
  { type: 'inverter_resume', label: 'Resume Inverter', category: 'protective', criticality: 'high', requiresWritesEnabled: true, commissioningOnly: false, confirmationText: 'This will resume the inverter from standby. The system will start producing power again.' },
  { type: 'stop_discharge', label: 'Stop Discharge', category: 'protective', criticality: 'high', requiresWritesEnabled: true, commissioningOnly: false, confirmationText: 'This will stop the battery from discharging. Solar production continues.' },
  { type: 'resume_discharge', label: 'Resume Discharge', category: 'protective', criticality: 'high', requiresWritesEnabled: true, commissioningOnly: false, confirmationText: 'This will resume normal battery discharge operations.' },

  // Commissioning-only
  { type: 'configure_devices', label: 'Configure Devices', category: 'commissioning', criticality: 'medium', requiresWritesEnabled: true, commissioningOnly: true },
];

/** WiFi signal quality thresholds */
export function getWifiQuality(rssi: number | null): { label: string; color: string } {
  if (rssi == null) return { label: 'Unknown', color: 'text-gray-400' };
  if (rssi > -65) return { label: 'Good', color: 'text-wt-green-primary' };
  if (rssi > -80) return { label: 'Weak', color: 'text-wt-amber-primary' };
  return { label: 'Critical', color: 'text-wt-red-primary' };
}

/** Format uptime in seconds to human-readable */
export function formatUptime(seconds: number | null): string {
  if (seconds == null || seconds < 0) return '--';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

// ---------------------------------------------------------------------------
// Deployments (OTA rollout)
// ---------------------------------------------------------------------------

export type DeploymentStrategy = 'immediate' | 'staged' | 'maintenance_window';
export type DeploymentStatus = 'pending' | 'in_progress' | 'paused' | 'completed' | 'cancelled' | 'failed';
export type DeploymentTargetStatus =
  | 'pending'
  | 'queued'
  | 'downloading'
  | 'installing'
  | 'success'
  | 'failed'
  | 'skipped'
  | 'rolled_back';

export interface GatewayDeployment {
  id: string;
  organization_id: string;
  firmware_release_id: string;
  name: string | null;
  strategy: DeploymentStrategy;
  staged_config: {
    stages?: number[];
    current_stage_index?: number;
    auto_advance?: boolean;
    health_check_minutes?: number;
  } | null;
  maintenance_window: {
    days?: string[];
    start?: string;
    end?: string;
    timezone?: string;
  } | null;
  status: DeploymentStatus;
  total_gateways: number;
  updated_gateways: number;
  failed_gateways: number;
  failure_threshold_pct: number;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  paused_at: string | null;
  created_by: string | null;
  firmware_version?: string;
  release_notes?: string | null;
}

export interface GatewayDeploymentTarget {
  id: string;
  deployment_id: string;
  gateway_id: string;
  stage: number;
  status: DeploymentTargetStatus;
  started_at: string | null;
  completed_at: string | null;
  error: string | null;
  previous_version: string | null;
  gateway_mac_address?: string | null;
  property_name?: string | null;
}

// ---------------------------------------------------------------------------
// Gateway profiles (Phase 4)
// ---------------------------------------------------------------------------

export interface GatewayProfileExpectedDevice {
  bus_address: number;
  protocol: 'modbus_rtu' | 'can';
  equipment_type: string;
  display_name?: string;
}

export interface GatewayProfilePropertyDefaults {
  grid_mode?: 'off_grid' | 'grid_tied' | 'hybrid' | 'grid_primary';
  battery_capacity_kwh?: number;
  battery_reserve_pct?: number;
  notification_rules_template?: string;
}

export interface GatewayProfile {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  poll_interval_ms: number;
  upload_interval_ms: number;
  heartbeat_interval_ms: number;
  command_poll_interval_ms: number;
  writes_enabled: boolean;
  ota_channel: 'pilot' | 'stable';
  expected_devices: GatewayProfileExpectedDevice[];
  default_property_config: GatewayProfilePropertyDefaults | null;
  auto_commission: boolean;
  auto_create_property: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}
